import * as React from 'react';

import classNames from 'classnames';
import { LazyLoadComponent } from 'react-lazy-load-image-component';

import { EMainMenuCounters, ERefsLeftMenu } from '@enums';

import { IMainMenuAppsStoreParams } from '@stores';

import { getConfig, URLS } from '@base/constants';
import { counters, getTranslation, translations } from '@base/utils';

import { MainMenuAppsSet, MainMenuBalance, MainMenuItem, MainMenuSet, MainMenuUser } from '@components';

import {
	IconCommunities,
	IconFeed,
	IconFriends,
	IconFull,
	IconGames,
	IconMusic,
	IconRegistration,
	IconSignIn,
	IconSignOut,
	IconVideo,
	IconWallet,
} from '@icons';

import { getSectionLink } from '../utils';

import { IUser } from '@interfaces';

import s from './MainMenuView.scss';

export interface IMainMenuViewProps {
	isVisible: boolean;
	currentUser: IUser;
	menuApps: IMainMenuAppsStoreParams;
	className?: string;
	mainMenuViewRef: React.RefObject<HTMLDivElement>;
	onLogOutClick?(event: React.MouseEvent<HTMLAnchorElement, MouseEvent>): void;
	onLogInClick?(event: React.MouseEvent<HTMLAnchorElement, MouseEvent>): void;
	onRegistrationClick?(event: React.MouseEvent<HTMLAnchorElement, MouseEvent>): void;
	onHide(): void;
}

export const MainMenuView: React.FC<IMainMenuViewProps> = ({
	className,
	onLogOutClick,
	onLogInClick,
	onRegistrationClick,
	currentUser,
	menuApps: {
		myApps,
		suggestApps,
	},
	isVisible,
	onHide,
	mainMenuViewRef,
}: IMainMenuViewProps) => {
	const t = getTranslation('components.MainMenu');
	const {
		sumMailiki,
		avatar,
		name,
		isMyCreated,
		dir,
	} = currentUser;
	const { apps, hosts } = getConfig();
	const appsCatalogUrl = apps?.pathname || URLS.APPS;
	const menuClassName = classNames(
		className,
		s.block,
		{ [s.visible]: isVisible },
	);

	const onMenuItemClick = () => {
		onHide();
	};

	return (
		<div
			className={menuClassName}
			ref={mainMenuViewRef}
		>
			<LazyLoadComponent visibleByDefault={!getConfig().isMobile}>
				<div className={s.innerWrapper}>
					{ /* Блок пользователя */}
					{isMyCreated && (
						<MainMenuSet>
							<MainMenuUser
								avatar={avatar?.url?.size45}
								link={dir || ''}
								name={name?.full}
								onClick={onHide}
							/>
						</MainMenuSet>
					)}

					<MainMenuSet>
						{isMyCreated &&
						<>
							{ /* Лента */}
							<MainMenuItem
								title={t('feed')}
								onClick={onMenuItemClick}
								link={getSectionLink(URLS.MAIN)}
								icon={IconFeed}
								counter={EMainMenuCounters.FEED}
							/>
							{ /* Друзья */}
							<MainMenuItem
								title={t('friends')}
								onClick={onMenuItemClick}
								link={getSectionLink(URLS.FRIENDS)}
								icon={IconFriends}
								counter={EMainMenuCounters.FRIENDS}
								// badge={<MainMenuBadge text={'2'} type={EMainMenuBadge.COUNTER}/>}
							/>
						</>
						}
						{ /* Видео */}
						<MainMenuItem
							title={t('video')}
							onClick={onMenuItemClick}
							link={getSectionLink(URLS.VIDEO)}
							icon={IconVideo}
							counter={EMainMenuCounters.VIDEO}
						/>
						{ /* Музыка */}
						<MainMenuItem
							title={t('music')}
							onClick={onMenuItemClick}
							link={getSectionLink(URLS.MUSIC)}
							icon={IconMusic}
							counter={EMainMenuCounters.MUSIC}
						/>
						{ /* Группы */}
						<MainMenuItem
							title={t('communities')}
							onClick={onMenuItemClick}
							link={getSectionLink(URLS.COMMUNITIES)}
							icon={IconCommunities}
							counter={EMainMenuCounters.COMMUNITIES}
						/>
						{ /* Игры */}
						<MainMenuItem
							title={t('apps')}
							onClick={onMenuItemClick}
							link={appsCatalogUrl}
							icon={IconGames}
							counter={EMainMenuCounters.GAMES_CATALOG}
							isOuterLink
						/>
					</MainMenuSet>

					{ /* Платежи */}
					{isMyCreated && (
						<MainMenuSet title={t('pays')}>
							<MainMenuItem
								title={t('score')}
								icon={IconWallet}
								badge={
									<MainMenuBalance
										className={s.balance}
										balance={sumMailiki}
									/>
								}
								counter={EMainMenuCounters.BALANCE}
								isNotLink
							/>
						</MainMenuSet>
					)}

					{ /* Ссылки на "Мои игры" */}
					{isMyCreated && !!myApps?.length &&
					<MainMenuAppsSet
						appsArray={myApps}
						title={translations.appCatalog('appsMy')}
						isMyCreated={isMyCreated}
					/>
					}

					{ /* Ссылки на игры раздела "Рекомендуем" */}
					{!!suggestApps?.length &&
					<MainMenuAppsSet
						appsArray={suggestApps}
						title={translations.common('suggest')}
						isMyCreated={isMyCreated}
						customRef={ERefsLeftMenu.GAMES_PROMO}
					/>
					}

					{ /* Ссылки на раздел "Полная версия" */}
					<MainMenuSet title={t('other')}>
						<MainMenuItem
							title={t('fullVersion')}
							onClick={onMenuItemClick}
							link={counters.getRbUrl(`${hosts.baseUrlDesktop}/stay_here`, EMainMenuCounters.FULL_VERSION)}
							icon={IconFull}
						/>
						{ /* Выход из профиля */}
						{isMyCreated
							? <MainMenuItem
								title={t('logout')}
								onClick={onLogOutClick}
								icon={IconSignOut}
								counter={EMainMenuCounters.LOGOUT}
							/>
							: <>
								{ /* Авторизация */}
								<MainMenuItem
									title={t('login')}
									onClick={onLogInClick}
									link={URLS.MAIN}
									icon={IconSignIn}
									counter={EMainMenuCounters.LOGIN}
								/>
								{ /* Регистрация */}
								<MainMenuItem
									title={t('registration')}
									onClick={onRegistrationClick}
									icon={IconRegistration}
									counter={EMainMenuCounters.REGISTRATION}
								/>
							</>
						}
					</MainMenuSet>
				</div>
			</LazyLoadComponent>
		</div>
	);
};
